<template>
    <div class="quote-history">

        <div v-if="loading" class="quote-history-loading">
            <i class="fa fa-spinner fa-spin"></i> Loading history...
        </div>

        <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

        <div v-else>
            <div v-if="rows.length === 0" class="quote-history-empty">
                <i class="fa fa-history"></i>
                <p>No history recorded for this quote yet.</p>
                <p class="text-muted">
                    The audit log was added in May 2026 — quotes created before then
                    won't have entries unless they've been edited since.
                </p>
            </div>

            <div v-else>
                <div class="quote-history-header">
                    <span><strong>{{ rows.length }}</strong> change{{ rows.length === 1 ? '' : 's' }}</span>
                    <span v-if="aiCount > 0" class="quote-history-ai-pill">
                        <i class="fa fa-magic"></i>
                        {{ aiCount }} AI {{ aiCount === 1 ? 'change' : 'changes' }}
                    </span>
                </div>

                <ol class="quote-history-timeline">
                    <li
                        v-for="row in rows"
                        :key="row.id"
                        class="quote-history-row"
                        :class="'qh-source-' + row.source"
                    >
                        <div class="qh-row-meta">
                            <span class="qh-source-badge" :title="sourceTitle(row.source)">
                                <i class="fa" :class="sourceIcon(row.source)"></i>
                                {{ sourceLabel(row.source) }}
                            </span>
                            <span class="qh-action">{{ humanAction(row.action) }}</span>
                            <span v-if="row.item_id" class="qh-item">item #{{ row.item_id }}</span>
                            <span class="qh-when" :title="row.created_at">{{ relativeTime(row.created_at) }}</span>
                        </div>

                        <div v-if="row.user" class="qh-row-user">
                            by <strong>{{ row.user.name }}</strong>
                        </div>

                        <div v-if="hasFieldChanges(row)" class="qh-changes">
                            <div
                                v-for="(change, field) in row.field_changes"
                                :key="field"
                                class="qh-change"
                            >
                                <span class="qh-field">{{ humanField(field) }}</span>
                                <span class="qh-from">{{ formatValue(change.from) }}</span>
                                <i class="fa fa-arrow-right qh-arrow"></i>
                                <span class="qh-to">{{ formatValue(change.to) }}</span>
                            </div>
                        </div>

                        <div v-if="row.notes" class="qh-notes">{{ row.notes }}</div>

                        <button
                            v-if="row.ai_session_id"
                            type="button"
                            class="qh-session-btn"
                            @click="openSession(row.ai_session_id)"
                        >
                            <i class="fa fa-comments"></i>
                            View AI conversation
                        </button>
                    </li>
                </ol>
            </div>
        </div>

        <ai-session-drawer
            v-if="openSessionId"
            :session="openSessionPayload"
            @close="openSessionId = null"
        ></ai-session-drawer>
    </div>
</template>

<script>
import AiSessionDrawer from './AiSessionDrawer.vue';

const SOURCE_LABELS = {
    web: 'Web',
    ai:  'AI',
    copy: 'Copy',
    import: 'Import',
    cli: 'CLI',
    queue: 'Queue',
};

const SOURCE_ICONS = {
    web:    'fa-pencil',
    ai:     'fa-magic',
    copy:   'fa-files-o',
    import: 'fa-upload',
    cli:    'fa-terminal',
    queue:  'fa-tasks',
};

const ACTION_LABELS = {
    'quote.created':         'Quote created',
    'quote.updated':         'Quote updated',
    'quote.deleted':         'Quote deleted',
    'quote.status_changed':  'Status changed',
    'quote.locked':          'Locked',
    'quote.unlocked':        'Unlocked',
    'item.created':          'Item added',
    'item.updated':          'Item updated',
    'item.options_changed':  'Item options changed',
    'item.deleted':          'Item deleted',
    'pdf.generated':         'PDF generated',
    'email.sent':            'Email sent',
    'ai.session_started':    'AI session started',
    'ai.session_completed':  'AI session completed',
    'ai.session_abandoned':  'AI session abandoned',
};

export default {
    components: { AiSessionDrawer },

    props: {
        quoteId: { type: Number, required: true },
    },

    data: () => ({
        loaded: false,
        loading: false,
        error: null,
        rows: [],
        sessionsById: {},
        openSessionId: null,
    }),

    computed: {
        aiCount() {
            return this.rows.filter(r => r.source === 'ai').length;
        },
        openSessionPayload() {
            return this.openSessionId ? (this.sessionsById[this.openSessionId] || null) : null;
        },
    },

    methods: {
        // Called from the parent blade when the History tab becomes visible.
        load() {
            if (this.loaded || this.loading) return;
            this.loading = true;
            this.error = null;
            window.axios.get(`/quote/${this.quoteId}/audit-log`)
                .then(res => {
                    const data = res.data;
                    this.rows = data.audit_log || [];
                    const map = {};
                    (data.ai_sessions || []).forEach(s => { map[s.id] = s; });
                    this.sessionsById = map;
                    this.loaded = true;
                })
                .catch(e => {
                    this.error = (e && e.message) || 'Failed to load history.';
                })
                .then(() => { this.loading = false; });
        },

        openSession(id) {
            this.openSessionId = id;
        },

        sourceLabel(s) { return SOURCE_LABELS[s] || s; },
        sourceIcon(s) { return SOURCE_ICONS[s] || 'fa-circle-o'; },
        sourceTitle(s) {
            return s === 'ai' ? 'Change made by the AI draft assistant' : 'Change made via ' + (SOURCE_LABELS[s] || s);
        },

        humanAction(a) {
            return ACTION_LABELS[a] || a;
        },

        humanField(field) {
            // options.glazing -> "options · glazing"
            return field.replace(/_/g, ' ').replace(/^options\./, 'options · ');
        },

        formatValue(v) {
            if (v === null || v === undefined) return '∅';
            if (v === '') return '(empty)';
            if (typeof v === 'boolean') return v ? 'yes' : 'no';
            if (typeof v === 'number') return String(v);
            if (typeof v === 'string') return v.length > 80 ? v.slice(0, 77) + '…' : v;
            try { return JSON.stringify(v); } catch (e) { return String(v); }
        },

        hasFieldChanges(row) {
            const fc = row.field_changes;
            return fc && typeof fc === 'object' && Object.keys(fc).length > 0;
        },

        relativeTime(iso) {
            if (!iso) return '';
            const then = new Date(iso).getTime();
            const now = Date.now();
            const sec = Math.max(1, Math.round((now - then) / 1000));
            if (sec < 60)  return sec + 's ago';
            if (sec < 3600) return Math.round(sec / 60) + 'm ago';
            if (sec < 86400) return Math.round(sec / 3600) + 'h ago';
            const day = Math.round(sec / 86400);
            if (day < 7) return day + 'd ago';
            return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        },
    },
};
</script>

<style>
.quote-history {
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    color: #1f2937;
    padding: 16px 0 40px;
}

.quote-history-loading,
.quote-history-empty {
    text-align: center;
    padding: 40px 20px;
    color: #6b7280;
}

.quote-history-empty .fa {
    font-size: 32px;
    opacity: 0.5;
    margin-bottom: 12px;
}

.quote-history-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    margin-bottom: 16px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
}

.quote-history-ai-pill {
    margin-left: auto;
    background: #ede9fe;
    color: #6d28d9;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
}

.quote-history-timeline {
    list-style: none;
    margin: 0;
    padding: 0;
    border-left: 2px solid #e5e7eb;
    padding-left: 18px;
}

.quote-history-row {
    position: relative;
    padding: 12px 14px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 10px;
}

.quote-history-row::before {
    content: "";
    position: absolute;
    left: -25px;
    top: 18px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #9ca3af;
}

.qh-source-ai::before     { border-color: #7c3aed; background: #ede9fe; }
.qh-source-web::before    { border-color: #2563eb; background: #dbeafe; }
.qh-source-copy::before   { border-color: #059669; background: #d1fae5; }
.qh-source-import::before { border-color: #d97706; background: #fed7aa; }

.qh-row-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: 13px;
}

.qh-source-badge {
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.qh-source-ai .qh-source-badge {
    background: #ede9fe;
    color: #6d28d9;
}

.qh-source-web .qh-source-badge {
    background: #dbeafe;
    color: #1e40af;
}

.qh-action {
    font-weight: 500;
    color: #1f2937;
}

.qh-item {
    color: #6b7280;
    font-size: 12px;
}

.qh-when {
    margin-left: auto;
    color: #6b7280;
    font-size: 12px;
}

.qh-row-user {
    margin-top: 4px;
    font-size: 12px;
    color: #6b7280;
}

.qh-changes {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #e5e7eb;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12.5px;
}

.qh-change {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.qh-field {
    color: #4b5563;
    font-weight: 500;
    min-width: 120px;
}

.qh-from {
    color: #6b7280;
    text-decoration: line-through;
    text-decoration-color: #d1d5db;
}

.qh-to {
    color: #1f2937;
    font-weight: 500;
}

.qh-arrow {
    color: #9ca3af;
    font-size: 10px;
}

.qh-notes {
    margin-top: 6px;
    font-size: 12px;
    color: #6b7280;
    font-style: italic;
}

.qh-session-btn {
    margin-top: 8px;
    background: #ede9fe;
    color: #6d28d9;
    border: 1px solid #c4b5fd;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition: background-color 0.12s;
}

.qh-session-btn:hover {
    background: #ddd6fe;
}
</style>
